import { useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import { Skel } from '../components/Skel';
import { Banner } from '../components/Banner';
import { EmptyState } from '../components/EmptyState';
import { Crumbs } from '../components/Crumbs';
import type { MonitoringSubscription, MonitoringAlert } from '../data/types';

interface MonitoringProps {
  /** JWT access token. Если null — показываем prompt про логин. */
  token?: string | null;
}

/**
 * Страница «Мои подписки» — CRUD над monitoring_subscriptions через /v1/monitoring.
 *
 * Требует JWT auth. Если token не задан — рендерим prompt (auth flow в migration позже).
 *
 * Tabs:
 *  - «Подписки» — список + кнопка «Добавить» + кнопка «Удалить» на каждой
 *  - «Алерты» — лента уведомлений с фильтром «только непрочитанные»
 */
export function Monitoring({ token }: MonitoringProps) {
  const [subs, setSubs] = useState<MonitoringSubscription[] | null>(null);
  const [alerts, setAlerts] = useState<MonitoringAlert[] | null>(null);
  const [tab, setTab] = useState<'subs' | 'alerts'>('subs');
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const loadSubs = useCallback(() => {
    if (!token) return;
    setError(null);
    const ac = new AbortController();
    api
      .monitoringList({ signal: ac.signal, token })
      .then((r) => setSubs(r.items))
      .catch((e) => {
        if (e.name !== 'AbortError') {
          setError(e instanceof ApiError ? `API ${e.status}: ${e.message}` : String(e));
        }
      });
    return () => ac.abort();
  }, [token]);

  const loadAlerts = useCallback(
    (unread: boolean) => {
      if (!token) return;
      setError(null);
      const ac = new AbortController();
      api
        .monitoringAlerts(unread, { signal: ac.signal, token })
        .then((r) => setAlerts(r.items))
        .catch((e) => {
          if (e.name !== 'AbortError') {
            setError(e instanceof ApiError ? `API ${e.status}` : String(e));
          }
        });
      return () => ac.abort();
    },
    [token]
  );

  useEffect(() => {
    if (tab === 'subs' && subs === null) loadSubs();
    if (tab === 'alerts' && alerts === null) loadAlerts(unreadOnly);
  }, [tab, subs, alerts, unreadOnly, loadSubs, loadAlerts]);

  // Не залогинен — показываем prompt
  if (!token) {
    return (
      <main className="container" style={{ paddingTop: 32 }}>
        <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Мониторинг' }]} />
        <h1 style={{ marginTop: 16, marginBottom: 16 }}>Мониторинг компаний</h1>
        <Banner tone="info" title="Требуется вход">
          Для управления подписками нужно войти в аккаунт. Подписки уведомляют об
          изменениях в ЕГРЮЛ, судах, банкротстве, санкциях и риск-скоринге.
        </Banner>
        <div
          style={{
            marginTop: 24,
            padding: 20,
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e6e2d6)',
            borderRadius: 12,
          }}
        >
          <h2
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 18,
              margin: '0 0 12px',
              color: 'var(--navy-700, #0b1736)',
            }}
          >
            Что отслеживается
          </h2>
          <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text, #0e1320)', lineHeight: 1.7 }}>
            <li>Изменения в ЕГРЮЛ (директор, адрес, ОКВЭД)</li>
            <li>Появление новых судов и исп. производств</li>
            <li>Банкротство, ликвидация</li>
            <li>Включение/исключение из санкционных списков</li>
            <li>Изменение риск-скоринга больше чем на N баллов (настраиваемый порог)</li>
            <li>Блокировки счетов, госконтракты</li>
          </ul>
        </div>
      </main>
    );
  }

  return (
    <main className="container" style={{ paddingTop: 32 }}>
      <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Мониторинг' }]} />

      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginTop: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0 }}>Мониторинг компаний</h1>
        {tab === 'subs' && (
          <button
            type="button"
            onClick={() => setShowAddForm((v) => !v)}
            style={primaryBtn}
          >
            {showAddForm ? '× закрыть' : '+ добавить подписку'}
          </button>
        )}
      </div>

      <div role="tablist" aria-label="Разделы мониторинга" style={tabsNav}>
        {(['subs', 'alerts'] as const).map((key) => {
          const labels = {
            subs: `Подписки${subs ? ` (${subs.length})` : ''}`,
            alerts: `Алерты${alerts ? ` (${alerts.length})` : ''}`,
          };
          return (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              type="button"
              onClick={() => setTab(key)}
              style={tabBtn(tab === key)}
            >
              {labels[key]}
            </button>
          );
        })}
      </div>

      {error && (
        <Banner tone="risk" title="Ошибка">
          {error}
        </Banner>
      )}

      {tab === 'subs' && (
        <SubsTab
          subs={subs}
          token={token}
          showAddForm={showAddForm}
          onAdded={() => {
            setShowAddForm(false);
            setSubs(null); // trigger reload
          }}
          onDeleted={() => setSubs(null)}
        />
      )}

      {tab === 'alerts' && (
        <AlertsTab
          alerts={alerts}
          unreadOnly={unreadOnly}
          onUnreadToggle={(v) => {
            setUnreadOnly(v);
            setAlerts(null); // trigger reload
          }}
          token={token}
          onMarkedRead={() => setAlerts(null)}
        />
      )}
    </main>
  );
}

function SubsTab({
  subs,
  token,
  showAddForm,
  onAdded,
  onDeleted,
}: {
  subs: MonitoringSubscription[] | null;
  token: string;
  showAddForm: boolean;
  onAdded: () => void;
  onDeleted: () => void;
}) {
  return (
    <section aria-label="Подписки">
      {showAddForm && <AddSubForm token={token} onAdded={onAdded} />}
      {subs === null && <Skel count={3} height={88} radius={8} />}
      {subs !== null && subs.length === 0 && !showAddForm && (
        <EmptyState
          icon="🔔"
          title="Подписок ещё нет"
          body="Нажмите «+ добавить подписку» и введите ИНН или slug компании, за которой хотите следить."
        />
      )}
      {subs !== null && subs.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {subs.map((sub) => (
            <SubItem key={sub.id} sub={sub} token={token} onDeleted={onDeleted} />
          ))}
        </ul>
      )}
    </section>
  );
}

function SubItem({
  sub,
  token,
  onDeleted,
}: {
  sub: MonitoringSubscription;
  token: string;
  onDeleted: () => void;
}) {
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!confirm(`Удалить подписку на ${sub.entity_name ?? sub.entity_slug}?`)) return;
    setDeleting(true);
    try {
      await api.monitoringDelete(sub.id, { token });
      onDeleted();
    } catch {
      setDeleting(false);
    }
  }

  const watchedTypes: string[] = [];
  if (sub.watches.egrul_changes) watchedTypes.push('ЕГРЮЛ');
  if (sub.watches.lawsuits) watchedTypes.push('Суды');
  if (sub.watches.enforcement) watchedTypes.push('Исп.произв.');
  if (sub.watches.bankruptcy) watchedTypes.push('Банкротство');
  if (sub.watches.sanctions) watchedTypes.push('Санкции');
  if (sub.watches.finance) watchedTypes.push('Финансы');
  if (sub.watches.procurement) watchedTypes.push('Закупки');
  if (sub.watches.account_blocks) watchedTypes.push('Блокировки');

  return (
    <li
      style={{
        background: 'var(--surface, #ffffff)',
        border: '1px solid var(--border, #e6e2d6)',
        borderRadius: 12,
        padding: 16,
        opacity: deleting ? 0.5 : 1,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <a
            href={
              sub.entity_type === 'company' || sub.entity_type === 'ip'
                ? `#/company/${sub.entity_slug}`
                : '#/'
            }
            style={{
              fontWeight: 600,
              fontSize: 16,
              color: 'var(--navy-700, #0b1736)',
              textDecoration: 'none',
              overflowWrap: 'anywhere',
            }}
          >
            {sub.entity_name ?? sub.entity_slug}
          </a>
          <div style={{ fontSize: 13, color: 'var(--muted, #6b7287)', marginTop: 4 }}>
            {sub.entity_type} · {sub.entity_slug}
          </div>
          {sub.custom_name && (
            <div style={{ fontSize: 13, marginTop: 6, fontStyle: 'italic' }}>
              «{sub.custom_name}»
            </div>
          )}
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              marginTop: 10,
            }}
          >
            {watchedTypes.map((t) => (
              <span
                key={t}
                style={{
                  background: 'var(--bg, #fbfaf7)',
                  color: 'var(--navy-500, #1a2d5e)',
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  border: '1px solid var(--border, #e6e2d6)',
                }}
              >
                {t}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted, #6b7287)', marginTop: 8 }}>
            Каналы: {sub.channels.join(', ')} · порог риска ±{sub.watches.risk_change_threshold}
          </div>
        </div>
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleting}
          style={dangerBtn}
          aria-label="Удалить подписку"
        >
          {deleting ? '…' : 'Удалить'}
        </button>
      </div>
    </li>
  );
}

function AddSubForm({ token, onAdded }: { token: string; onAdded: () => void }) {
  const [entitySlug, setEntitySlug] = useState('');
  const [customName, setCustomName] = useState('');
  const [watchFinance, setWatchFinance] = useState(false);
  const [watchProcurement, setWatchProcurement] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!entitySlug.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.monitoringCreate(
        {
          entity_type: 'company',
          entity_slug: entitySlug.trim(),
          watch_finance: watchFinance,
          // watch_procurement отдельно не в типе body, но можно слать
          ...(watchProcurement ? ({ watch_procurement: true } as Record<string, unknown>) : {}),
          ...(customName ? ({ custom_name: customName } as Record<string, unknown>) : {}),
        },
        { token }
      );
      setEntitySlug('');
      setCustomName('');
      setWatchFinance(false);
      setWatchProcurement(false);
      onAdded();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: 'var(--surface, #ffffff)',
        border: '1px solid var(--gold-500, #c79a2c)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <h3 style={{ margin: 0, fontSize: 16, color: 'var(--navy-700, #0b1736)' }}>
        Новая подписка
      </h3>
      <input
        type="text"
        value={entitySlug}
        onChange={(e) => setEntitySlug(e.target.value)}
        placeholder="ИНН или slug компании"
        aria-label="ИНН или slug"
        required
        style={inputStyle}
      />
      <input
        type="text"
        value={customName}
        onChange={(e) => setCustomName(e.target.value)}
        placeholder="Название (для своих заметок, опционально)"
        aria-label="Custom название"
        style={inputStyle}
      />
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={watchFinance}
            onChange={(e) => setWatchFinance(e.target.checked)}
          />
          Следить за финансами
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={watchProcurement}
            onChange={(e) => setWatchProcurement(e.target.checked)}
          />
          Следить за закупками
        </label>
      </div>
      <p style={{ fontSize: 12, color: 'var(--muted, #6b7287)', margin: 0 }}>
        Базовое слежение (ЕГРЮЛ, суды, исп.произв., банкротство, санкции) включено по умолчанию.
      </p>
      {error && <Banner tone="risk">{error}</Banner>}
      <button type="submit" disabled={submitting} style={primaryBtn}>
        {submitting ? 'Создаём…' : 'Подписаться'}
      </button>
    </form>
  );
}

function AlertsTab({
  alerts,
  unreadOnly,
  onUnreadToggle,
  token,
  onMarkedRead,
}: {
  alerts: MonitoringAlert[] | null;
  unreadOnly: boolean;
  onUnreadToggle: (v: boolean) => void;
  token: string;
  onMarkedRead: () => void;
}) {
  return (
    <section aria-label="Алерты">
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={unreadOnly}
          onChange={(e) => onUnreadToggle(e.target.checked)}
        />
        Только непрочитанные
      </label>
      {alerts === null && <Skel count={3} height={64} radius={8} />}
      {alerts !== null && alerts.length === 0 && (
        <EmptyState
          icon="✓"
          title="Алертов нет"
          body={
            unreadOnly
              ? 'Все уведомления прочитаны.'
              : 'У вас пока нет уведомлений. Они появятся при изменениях по подпискам.'
          }
        />
      )}
      {alerts !== null && alerts.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {alerts.map((a) => (
            <AlertItem key={a.id} alert={a} token={token} onMarkedRead={onMarkedRead} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AlertItem({
  alert,
  token,
  onMarkedRead,
}: {
  alert: MonitoringAlert;
  token: string;
  onMarkedRead: () => void;
}) {
  const isUnread = alert.read_at === null;
  const [marking, setMarking] = useState(false);

  async function markRead() {
    if (!isUnread) return;
    setMarking(true);
    try {
      // POST /v1/monitoring/alerts/{id}/read
      await fetch(`/v1/monitoring/alerts/${alert.id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      onMarkedRead();
    } catch {
      setMarking(false);
    }
  }

  return (
    <li
      style={{
        background: isUnread ? '#fff7e6' : 'var(--surface, #ffffff)',
        border: `1px solid ${isUnread ? 'var(--gold-500, #c79a2c)' : 'var(--border, #e6e2d6)'}`,
        borderRadius: 8,
        padding: 12,
        opacity: marking ? 0.5 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>
            {alert.entity_type} · <a href={`#/company/${alert.entity_slug}`}>{alert.entity_slug}</a>
          </div>
          <div style={{ fontWeight: 600, marginTop: 4, overflowWrap: 'anywhere' }}>
            {alert.alert_type}
          </div>
          <pre
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 11,
              background: 'var(--bg, #fbfaf7)',
              padding: 8,
              borderRadius: 6,
              marginTop: 6,
              overflowX: 'auto',
            }}
          >
            {JSON.stringify(alert.payload, null, 2).slice(0, 500)}
          </pre>
          <div style={{ fontSize: 11, color: 'var(--muted, #6b7287)', marginTop: 6 }}>
            {new Date(alert.created_at).toLocaleString('ru-RU')}
          </div>
        </div>
        {isUnread && (
          <button type="button" onClick={markRead} disabled={marking} style={smallBtn}>
            ✓ прочитано
          </button>
        )}
      </div>
    </li>
  );
}

const tabsNav: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  borderBottom: '1px solid var(--border, #e6e2d6)',
  marginBottom: 24,
  overflowX: 'auto',
};
function tabBtn(active: boolean): React.CSSProperties {
  return {
    padding: '10px 16px',
    border: 'none',
    background: 'transparent',
    fontSize: 14,
    fontWeight: active ? 600 : 500,
    color: active ? 'var(--navy-700, #0b1736)' : 'var(--muted, #6b7287)',
    borderBottom: `2px solid ${active ? 'var(--gold-500, #c79a2c)' : 'transparent'}`,
    cursor: 'pointer',
    flexShrink: 0,
    marginBottom: -1,
  };
}
const inputStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: 14,
  border: '1px solid var(--border, #e6e2d6)',
  borderRadius: 8,
  background: 'var(--surface, #ffffff)',
};
const primaryBtn: React.CSSProperties = {
  padding: '10px 20px',
  background: 'var(--navy-700, #0b1736)',
  color: '#ffffff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
};
const dangerBtn: React.CSSProperties = {
  padding: '6px 14px',
  background: 'transparent',
  color: '#a31919',
  border: '1px solid #e89191',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  flexShrink: 0,
};
const smallBtn: React.CSSProperties = {
  padding: '4px 10px',
  background: 'transparent',
  color: 'var(--navy-500, #1a2d5e)',
  border: '1px solid var(--border, #e6e2d6)',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  flexShrink: 0,
};
